import { trans } from "@mongez/localization";
import { useClickOutside } from "core/shared";
import { userAtom } from "core/user";
import { LogOut, Map, MapPinCheckInside } from "lucide-react";
import { useState } from "react";
import { BaseLink, User } from "~/design-system/components";
import { Button } from "~/design-system/components/button";
import { logoutPortal } from "~/modules/my-account/components";
import { headerMenuAtom, searchAtom } from "~/modules/shared/atoms";
import { URLS } from "~/shared/urls";

const userMenu = [
  {
    title: "myAccount",
    href: URLS.account.root,
    icon: <User className="size-7" />,
  },
  {
    title: "myAddresses",
    href: URLS.account.addresses,
    icon: <Map />,
  },
  {
    title: "myOrders",
    href: URLS.account.orders,
    icon: <MapPinCheckInside />,
  },
];

const guestMenu = [
  {
    title: "login",
    href: URLS.auth.login,
  },
  {
    title: "register",
    href: URLS.auth.register,
  },
];

export function UserButton() {
  const user = userAtom.useValue();
  const isUser = user.userType === "user";
  const [opened, setOpened] = useState(false);

  const containerRef = useClickOutside(() => setOpened(false));

  return (
    <div ref={containerRef} className="relative flex">
      <Button
        className="hover:bg-primary-light rounded-full border border-transparent p-0 hover:border-black lg:p-2"
        onClick={() => {
          searchAtom.close();
          headerMenuAtom.close();
          setOpened(!opened);
        }}
        title={trans("user")}
        aria-label={trans("user")}
      >
        <User className="size-7" />
      </Button>
      {opened && (
        <div className="animate-fade absolute top-[calc(100%+0.5rem)] z-50 w-[250px] rounded-2xl border bg-white p-2 ltr:right-0 rtl:left-0">
          {isUser ? (
            <div className="flex flex-col gap-2">
              {userMenu.map((item) => (
                <UserMenuItem
                  key={item.title}
                  title={item.title}
                  href={item.href}
                  icon={item.icon}
                  onClick={() => setOpened(false)}
                />
              ))}
              <span className="bg-border h-[1px] w-full rounded-full" />
              <Button
                className="hover:bg-primary-light flex items-center gap-2 rounded-xl p-2 font-medium"
                onClick={() => {
                  logoutPortal.open();
                  setOpened(false);
                }}
                title={trans("logout")}
                aria-label={trans("logout")}
              >
                <LogOut />
                {trans("logout")}
              </Button>
            </div>
          ) : (
            guestMenu.map((item) => (
              <UserMenuItem
                key={item.title}
                title={item.title}
                href={item.href}
                onClick={() => setOpened(false)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

type UserMenuItemProps = {
  onClick?: () => void;
  title: string;
  href: string;
  icon?: React.ReactNode;
};

function UserMenuItem({ title, href, icon, onClick }: UserMenuItemProps) {
  return (
    <BaseLink
      to={href}
      onClick={onClick}
      className="hover:bg-primary-light flex items-center gap-2 rounded-xl p-2 font-medium"
    >
      {icon}
      {trans(title)}
    </BaseLink>
  );
}
